import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Chip,
  Box,
} from "@mui/material";
import { ParkList, InvestorList } from "../../../Shared/PreRenderedComponents";
import { Link } from "react-router-dom";
import { Add, Delete } from "@mui/icons-material";
import { GetDataFromApiWithParamsAndGetResponse } from "../../../../../services/api/ExecuteApiRequests";

const Step1 = ({
  formData,
  handleChange,
  selectedPark,
  setSelectedPark,
  parcelInPark,
  shedInPark, // NEW: Add shedInPark prop
  selectedInvestor,
  setSelectedInvestor,
  nextStep,
  isRenewal = false,
}) => {
  // State to manage selected UPINs
  const [selectedUpins, setSelectedUpins] = useState(formData.upin ? (Array.isArray(formData.upin) ? formData.upin : [formData.upin]) : []);
  
  // NEW: State for selected shed
  const [selectedShedId, setSelectedShedId] = useState("");

  // Effect to update formData when selectedUpins changes
  useEffect(() => {
    handleChange({ target: { name: "upin", value: selectedUpins } });
  }, [selectedUpins]);

  // Effect to update tenant_id when selectedInvestor changes
  useEffect(() => {
    if (selectedInvestor) {
      const tenantId = selectedInvestor.investor_id;
      if (tenantId) {
        handleChange({ target: { name: "tenant_id", value: tenantId } });
        console.log("Tenant ID set to:", tenantId);
        formData.tenant_id = tenantId;
      }
    }
  }, [selectedInvestor]);

  // NEW: Function to handle shed selection
  const handleShedSelection = (shedId) => {
    setSelectedShedId(shedId);
    
    // Find the selected shed from shedInPark
    const selectedShed = shedInPark?.find(shed => shed.shed_id === shedId);
    
    if (selectedShed && selectedShed.upin) {
      // Clear existing selections and add the new UPIN
      const newUpins = [selectedShed.upin];
      setSelectedUpins(newUpins);
      
      // Set ownership type to "rent" for shed
      formData.ownership_type = "rent";
      handleChange({ target: { name: "ownership_type", value: "rent" } });
      
      console.log(`Selected shed: ${shedId} with UPIN: ${selectedShed.upin}`);
    }
  };

  // Function to remove a UPIN
  const handleRemoveUpin = upinToRemove => {
    const filteredUpins = selectedUpins.filter(upin => upin !== upinToRemove);
    setSelectedUpins(filteredUpins);
    formData.upin = filteredUpins;
    
    // Clear selected shed if UPIN is removed
    if (filteredUpins.length === 0) {
      setSelectedShedId("");
      formData.ownership_type = "";
    } else {
      formData.ownership_type = filteredUpins.length === 0 ? "" : "rent";
    }
  };

  useEffect(() => {
    const getNewId = async () => {
      try {
        if (selectedPark) {
          const result = (await GetDataFromApiWithParamsAndGetResponse(`agreements/${selectedPark.id}/getNewId`)) || "";
          console.log("new id is ", result.data.new_id);
          formData.agreement_id = result.data.new_id;
        } else {
          formData.agreement_id = "";
        }
      } catch (error) {
        console.log(error);
      }
    };
    getNewId();
  }, [selectedPark]);

  // NEW: Reset shed selection when park changes
  useEffect(() => {
    setSelectedShedId("");
    setSelectedUpins([]);
  }, [selectedPark]);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Step 1: Basic Information {isRenewal && "(Renewal)"}</Typography>
        
        {isRenewal && formData.original_agreement_id && (
          <Box sx={{ 
            backgroundColor: 'info.light', 
            p: 1, 
            borderRadius: 1, 
            mt: 1,
            border: '1px solid',
            borderColor: 'info.main'
          }}>
            <Typography variant="body2" sx={{ color: 'info.dark', fontWeight: 'medium' }}>
              Renewing Agreement: <strong>{formData.original_agreement_id}</strong>
            </Typography>
          </Box>
        )}
      </Grid>
      
      <Grid item xs={12} md={6}>
        <ParkList selectedPark={selectedPark} setSelectedPark={setSelectedPark} />
      </Grid>
      
      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          type="text"
          label="Agreement Id"
          id="agreement_id"
          name="agreement_id"
          value={formData.agreement_id}
          onChange={handleChange}
          required
          helperText={isRenewal ? "New agreement ID will be generated for the renewal" : ""}
        />
      </Grid>
      
      {/* NEW: Shed Number Dropdown - Replaces UPIN dropdown */}
      <Grid item xs={6}>
        <FormControl fullWidth>
          <InputLabel htmlFor="shed_id">Shed Number (Select a shed)</InputLabel>
          <Select
            labelId="shed_id"
            id="shed_id"
            name="shed_id"
            value={selectedShedId}
            onChange={e => handleShedSelection(e.target.value)}
            disabled={!selectedPark || !shedInPark || shedInPark.length === 0}
          >
            {shedInPark && shedInPark.length > 0 ? (
              shedInPark.map(shed => (
                <MenuItem key={shed.shed_id} value={shed.shed_id}>
                  {shed.shed_number || shed.shed_id}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled value="">
                {selectedPark ? "No sheds available in this park" : "Select a park first"}
              </MenuItem>
            )}
          </Select>
        </FormControl>
      </Grid>

      {/* Display selected UPIN */}
      <Grid item xs={6}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selectedUpins?.length > 0 ? (
            selectedUpins.map(upin => (
              <Chip
                sx={{
                  height: "auto",
                  "& .MuiChip-label": {
                    display: "block",
                    whiteSpace: "normal",
                  },
                }}
                deleteIcon={<Delete sx={{ fill: "red" }} />}
                color="success"
                variant="outlined"
                key={upin}
                label={`UPIN: ${upin}`}
                onDelete={() => handleRemoveUpin(upin)}
              />
            ))
          ) : (
            <Typography variant="body2" color="text.secondary">
              No shed selected
            </Typography>
          )}
        </Box>
      </Grid>
      
      <Grid item xs={9} md={9}>
        <InvestorList selectedInvestor={selectedInvestor} setSelectedInvestor={setSelectedInvestor} />
        
        {isRenewal && selectedInvestor && (
          <Box sx={{ mt: 1, p: 1, backgroundColor: 'success.light', borderRadius: 1 }}>
            <Typography variant="body2" sx={{ color: 'success.dark' }}>
              Investor: <strong>{selectedInvestor.company_name}</strong>
            </Typography>
          </Box>
        )}
      </Grid>
      
      <Grid item xs={3} md={3} display="flex" alignItems="center">
        <Button variant="outlined" component={Link} to="/registerInvestor" startIcon={<Add />}>
          Register New Investor
        </Button>
      </Grid>
      
      <Grid item xs={12}>
        <FormControl fullWidth>
          <InputLabel id="ownership_type-label">Tenure Type</InputLabel>
          <Select
            labelId="ownership_type-label"
            id="ownership_type"
            name="ownership_type"
            value={formData.ownership_type}
            onChange={handleChange}
            disabled={selectedUpins.length > 0} // Lock to "rent" when shed is selected
          >
            <MenuItem value="rent">Shed Rent</MenuItem>
            <MenuItem value="lease">Service Land Lease</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      
      <Grid item xs={12}>
        <Box display="flex" justifyContent="flex-end">
          <Button variant="contained" onClick={nextStep}>
            <Typography color="inherit">Next</Typography>
          </Button>
        </Box>
      </Grid>
    </Grid>
  );
};

export default Step1;